import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from "react";
import { useAppSelector } from "../../hooks/redux";
import { useTheme } from "../../hooks/useTheme";
import { useMapInitialization } from "../../hooks/map/useMapInitialization";
import { useMarkerManager } from "../../hooks/map/useMarkerManager";
import { useMapControls } from "../../hooks/map/useMapControls";
import { MapProps } from "../../types/map";
import { PaywallModal } from "../PayWallModal/PayWallModal";
import styles from "./Map.module.css";
import { PlusIcon, MinusIcon, LayersIcon } from "../icons";
import { MarkerPopup } from "../MarkerPopup/MarkerPopup";
import { SubscriptionBanner } from "../SubscriptionBanner/SubscriptionBanner";
import { AnnouncementModal } from "../AnnouncementModal/AnnouncementModal";
import { MapFilters } from "../MapFilters/MapFilters";
//import { getPaywallType } from "../../utils/abTest";
import { ProfileFloatingButton } from "../ProfileFloatingButton/ProfileFloatingButton";
import { AddMarkerButton } from "../AddMarkerButton/AddMarkerButton";
import { useUserLocation } from "../../hooks/map/useUserLocation";
import { LocationIcon } from "../icons/location/LocationIcon";
import { UserLocationMarker } from "./UserLocationMarker/UserLocationMarker";
import CupImage from "../icons/contest/Cup.png";
import PresentImage from "../GiftSubscription/icons/present.png";
import { useBackButton } from "../../hooks/useBackButton";
import { ScreenshotWatermark } from "../ScreenshotWatermark/ScreenshotWatermark";

export const Map: React.FC<MapProps> = ({
  onMapReady,
  isPremium,
  userId,
  cloudPaymentsPublicKey,
  onSubscriptionSuccess,
  activedModal,
  //abTests,
  citySettings,
  contestData,
  onContestClick,
  onGiftClick,
  onProfileClick,
  userAvatar,
  userName,
  onLocationClarificationRequest,
  onShowToast,
  isScreenMode = false,
  initialZoom,
  onAddMarkerRequest,
  isEventReportsEnabled = false,
}) => {
  console.log("🗺️ Map props:", {
    isPremium,
    userId,
    cloudPaymentsPublicKey: !!cloudPaymentsPublicKey,
    onSubscriptionSuccess: !!onSubscriptionSuccess,
  });

  //const paywallType = getPaywallType(abTests);

  const {
    selectedCity,
    mapToken,
    isTrafficVisible,
    events,
    webcams,
    userPremium,
    isFiltersLoaded,
    userData,
  } = useAppSelector((state) => state.map);
  const { myGifts } = useAppSelector((state) => state.gifts);
  const { theme } = useTheme();

  // Проверяем наличие оплаченных и неактивированных подарков
  const hasPaidGifts = myGifts.some(
    (gift) => gift.is_paid && !gift.is_activated,
  );
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const handleMapReady = useCallback(() => {
    onMapReady?.();
  }, [onMapReady]);
  // Инициализация карты
  const {
    mapRef,
    isMapReady,
    mapInstance,
    mapgl,
    currentCityId,
    initMap,
    updateMapStyle,
    destroyMap,
  } = useMapInitialization();

  const [showLocationDeniedModal, setShowLocationDeniedModal] = useState(false);
  const [isMapFiltersOpen, setIsMapFiltersOpen] = useState(false);

  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [hasShownAnnouncement, setHasShownAnnouncement] = useState(false);

  const closeFilters = useCallback(() => {
    setIsMapFiltersOpen(false);
  }, []);

  // Управление маркерами
  const {
    selectedMarkerId,
    markerModal,
    setMarkerModal,
    setSelectedMarkerId,
    addMarkers,
    clearAllMarkers,
    resetAllMarkersSelection,
    updateMarkerOpacity,
  } = useMarkerManager(mapInstance, mapgl, isMapReady, theme, closeFilters);

  // Управление элементами карты
  const { handleZoomIn, handleZoomOut, toggleTraffic, updateMapSize } =
    useMapControls(mapInstance, isMapReady);

  const {
    position: userPosition,
    heading: userHeading,
    centerOnUser,
    hasAccess,
  } = useUserLocation({
    loggingInterval: 5000,
    onAccessDenied: () => setShowLocationDeniedModal(true),
  });

  const handleClarifyLocation = useCallback(
    (data: {
      eventId?: number;
      locationId?: number;
      title: string;
      coordinates: [number, number];
      initialCoordinates?: [number, number];
    }) => {
      console.log("🎯 Передаем запрос на уточнение локации в App");
      console.log("📍 Исходные координаты:", data.initialCoordinates);

      onLocationClarificationRequest?.(data);

      setMarkerModal({ isOpen: false, data: null });
    },
    [markerModal.data, setMarkerModal, onLocationClarificationRequest],
  );
  const handleAddMarker = useCallback(
    (type: "dps" | "clean" | "dtp") => {
      if (!mapInstance || !isMapReady) return;

      try {
        const center = mapInstance.getCenter();
        const coordinates: [number, number] = [center[0], center[1]];

        onAddMarkerRequest?.(type, coordinates);
      } catch (error) {
        console.warn("Ошибка получения координат:", error);
      }
    },
    [mapInstance, isMapReady, onAddMarkerRequest],
  );

  // Обработчик кнопки геолокации
  const handleLocationButtonClick = useCallback(async () => {
    console.log("🎯 Центрирование на пользователе");

    const position = await centerOnUser();

    if (position && mapInstance) {
      try {
        mapInstance.setCenter(position);

        if (window.Telegram?.WebApp?.HapticFeedback) {
          window.Telegram.WebApp.HapticFeedback.impactOccurred("medium");
        }
      } catch (error) {
        console.warn("Ошибка центрирования:", error);
      }
    }
  }, [centerOnUser, mapInstance]);

  // Инициализация карты при изменении города или токена
  useEffect(() => {
    console.log("🔄 useEffect initMap сработал:", {
      selectedCity: selectedCity?.name,
      cityId: selectedCity?.id,
      currentCityId,
      hasMap: !!mapInstance,
    });

    if (selectedCity && mapToken) {
      initMap(
        selectedCity,
        mapToken,
        isTrafficVisible,
        theme,
        clearAllMarkers,
        events,
        webcams,
        handleMapReady,
        initialZoom,
      );
    }
  }, [selectedCity?.id, mapToken, handleMapReady]); // ✅ Убрали theme и isTrafficVisible - они управляются отдельными useEffect
  useEffect(() => {
    const actualCitySettings = selectedCity?.settings;

    if (
      isMapReady &&
      mapInstance &&
      mapgl &&
      selectedCity &&
      currentCityId === selectedCity.id &&
      actualCitySettings &&
      actualCitySettings.is_map_enabled !== undefined &&
      isFiltersLoaded // Дожидаемся загрузки фильтров из storage
    ) {
      console.log(
        "✅ [MAP] Добавляем маркеры с актуальными настройками:",
        actualCitySettings,
      );

      addMarkers(
        events || [],
        webcams || [],
        onMapReady,
        true,
        actualCitySettings,
      );

      if (userPremium && actualCitySettings?.anounce && !hasShownAnnouncement) {
        setShowAnnouncement(true);
        setHasShownAnnouncement(true);
      } else if (!actualCitySettings?.anounce) {
        setShowAnnouncement(false);
      }
    }
  }, [
    events,
    webcams,
    isMapReady,
    currentCityId,
    selectedCity?.id,
    selectedCity?.settings,
    userPremium,
    isFiltersLoaded, // Добавляем в зависимости
    addMarkers,
    onMapReady,
  ]);
  // Обновление стиля карты при изменении темы
  useEffect(() => {
    console.log("🎨 Проверка стиля карты при смене темы:", theme);

    if (
      mapInstance &&
      isMapReady &&
      selectedCity &&
      currentCityId === selectedCity.id
    ) {
      const styleUpdated = updateMapStyle(theme);

      if (!styleUpdated) {
        console.log("🔄 Fallback: требуется пересоздание карты");
        // Вместо немедленного пересоздания, помечаем что нужно обновить
        clearAllMarkers();
        destroyMap();

        // Инициируем пересоздание через небольшую задержку
        setTimeout(() => {
          if (selectedCity && mapToken) {
            initMap(
              selectedCity,
              mapToken,
              isTrafficVisible,
              theme,
              clearAllMarkers,
              events,
              webcams,
              handleMapReady,
              initialZoom,
            );
          }
        }, 100);
      }
    }
  }, [theme]); // Только theme в зависимостях

  // Управление трафиком
  useEffect(() => {
    if (mapInstance && isMapReady && currentCityId === selectedCity?.id) {
      toggleTraffic(isTrafficVisible);
    }
  }, [isTrafficVisible, currentCityId, selectedCity?.id]);

  // Сброс выделения маркеров при закрытии модального окна
  useEffect(() => {
    if (!markerModal.isOpen && selectedMarkerId) {
      resetAllMarkersSelection();
      setSelectedMarkerId(null);
    }
  }, [markerModal.isOpen, selectedMarkerId]);

  // Обновление прозрачности маркеров
  useEffect(() => {
    if (!isMapReady) return;

    const interval = setInterval(updateMarkerOpacity, 60 * 1000);
    updateMarkerOpacity();

    return () => clearInterval(interval);
  }, [isMapReady, updateMarkerOpacity]);

  // Обработка изменения размера viewport
  useEffect(() => {
    const handleResize = () => updateMapSize();
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTimeout(updateMapSize, 200);
      }
    };

    const handleTelegramViewportChange = () => {
      setTimeout(updateMapSize, 150);
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.onEvent(
        "viewportChanged",
        handleTelegramViewportChange,
      );
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.offEvent(
          "viewportChanged",
          handleTelegramViewportChange,
        );
      }
    };
  }, [updateMapSize]);

  useEffect(() => {
    if (selectedCity && isMapReady && mapInstance) {
      const timer = setTimeout(() => {
        try {
          mapInstance.invalidateSize();
          mapInstance.setCenter([
            selectedCity.longitude,
            selectedCity.latitude,
          ]);
        } catch (error) {
          console.warn(
            "Ошибка при обновлении карты после смены города:",
            error,
          );
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [selectedCity, isMapReady, mapInstance]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      console.log("🧹 Очистка карты при размонтировании компонента");
      clearAllMarkers();
      destroyMap();
    };
  }, []);

  if (!selectedCity) {
    return null;
  }
  const handleCloseAnnouncement = useCallback(() => {
    setShowAnnouncement(false);
  }, []);

  const handleFiltersToggle = useCallback(() => {
    setIsMapFiltersOpen((prev) => {
      const willBeOpen = !prev;

      // При открытии фильтров закрываем popup и снимаем выделение
      if (willBeOpen) {
        setMarkerModal({ isOpen: false, data: null });
        resetAllMarkersSelection();
        setSelectedMarkerId(null);
      }

      return willBeOpen;
    });
  }, [setMarkerModal, resetAllMarkersSelection, setSelectedMarkerId]);

  // Обработчики для навигации
  const handleBackFromMarkerPopup = useCallback(() => {
    setMarkerModal({ isOpen: false, data: null });
    resetAllMarkersSelection();
    setSelectedMarkerId(null);
  }, [setMarkerModal, resetAllMarkersSelection, setSelectedMarkerId]);

  const handleBackFromMapFilters = useCallback(() => {
    setIsMapFiltersOpen(false);
  }, []);

  // Конфигурация навигации (приоритет сверху вниз)
  const navigationConfig = useMemo(() => {
    // Если открыт popup маркера - он имеет наивысший приоритет
    if (markerModal.isOpen) {
      return { isOpen: true, onBack: handleBackFromMarkerPopup };
    }
    // Если открыты фильтры
    if (isMapFiltersOpen) {
      return { isOpen: true, onBack: handleBackFromMapFilters };
    }
    // Нет открытых модалок
    return { isOpen: false, onBack: () => {} };
  }, [
    markerModal.isOpen,
    isMapFiltersOpen,
    handleBackFromMarkerPopup,
    handleBackFromMapFilters,
  ]);

  // Подключаем нативную кнопку назад
  useBackButton({
    isOpen: navigationConfig.isOpen,
    onBack: navigationConfig.onBack,
  });

  useEffect(() => {
    setHasShownAnnouncement(false);
    setShowAnnouncement(false);
  }, [selectedCity?.id]);

  return (
    <div ref={mapContainerRef} className={styles.container}>
      <div
        ref={mapRef}
        className={`${styles.map} ${!isPremium ? styles.blurred : ""}`}
      />

      {isMapReady && !isScreenMode && (
        <div
          className={`${styles.controls} ${!isPremium ? styles.disabled : ""}`}
        >
          <div
            className={`${styles.zoomGroup} ${
              theme === "dark" ? styles.dark : ""
            }`}
          >
            <button
              className={`${styles.controlButton} ${styles.zoomInButton}`}
              onClick={handleZoomIn}
              disabled={!isPremium}
            >
              <PlusIcon width="24" height="24" />
            </button>
            <button
              className={`${styles.controlButton} ${styles.zoomOutButton}`}
              onClick={handleZoomOut}
              disabled={!isPremium}
            >
              <MinusIcon width="24" height="24" />
            </button>
          </div>

          <button
            className={`${styles.controlButton} ${styles.trafficButton} ${
              theme === "dark" ? styles.dark : ""
            } ${isMapFiltersOpen ? styles.active : ""}`}
            onClick={handleFiltersToggle}
            disabled={!isPremium}
          >
            <LayersIcon width="24" height="24" />
          </button>
          {/* НОВАЯ КНОПКА ГЕОЛОКАЦИИ */}
          <button
            className={`${styles.controlButton} ${styles.locationButton} ${
              theme === "dark" ? styles.dark : ""
            }`}
            onClick={handleLocationButtonClick}
            disabled={!isPremium}
          >
            <LocationIcon width="24" height="24" />
          </button>
        </div>
      )}

      {!isPremium && cloudPaymentsPublicKey && (
        <>
          {/* {paywallType === "B" ? ( */}
          {true ? (
            // Версия B - показываем PaywallModal
            <PaywallModal onOpenFullPaywall={() => activedModal(true)} />
          ) : (
            // Версия A - показываем SubscriptionBanner
            <SubscriptionBanner
              userId={userId!}
              cloudPaymentsPublicKey={cloudPaymentsPublicKey!}
              onSubscriptionSuccess={onSubscriptionSuccess}
            />
          )}
        </>
      )}
      {/* Модальное окно объявления */}
      <AnnouncementModal
        isOpen={showAnnouncement}
        announcement={citySettings?.anounce || ""}
        onClose={handleCloseAnnouncement}
      />
      <AnnouncementModal
        isOpen={showLocationDeniedModal}
        announcement="📍 Геолокация отключена.

Чтобы видеть себя на карте, включите доступ к геопозиции в настройках устройства.

⚙️ Настройки → Telegram → Геолокация → Разрешить"
        onClose={() => setShowLocationDeniedModal(false)}
      />
      <MarkerPopup
        isOpen={markerModal.isOpen}
        onClose={() => setMarkerModal({ isOpen: false, data: null })}
        markerData={markerModal.data}
        onClarifyLocation={handleClarifyLocation}
        onShowToast={onShowToast}
      />
      {/* Плавающая кнопка подарка */}
      {!isScreenMode && hasPaidGifts && (
        <div
          className={styles.giftFloatingButton}
          style={{
            bottom: contestData
              ? "calc(260px + env(safe-area-inset-bottom))"
              : "calc(100px + env(safe-area-inset-bottom))",
          }}
          onClick={() => {
            onGiftClick?.();
          }}
        >
          <div className={styles.giftBadgeContainer}>
            <div className={styles.giftBadge}>
              <div className={styles.giftBadgeText}>
                Подарок готов к отправке
              </div>
            </div>
            <div className={styles.giftTail} />
          </div>

          <img
            className={styles.giftPresentImage}
            src={PresentImage}
            alt="Подарок"
          />
        </div>
      )}

      {/* Плавающая кнопка конкурса */}
      {!isScreenMode && contestData && (
        <div
          className={styles.contestFloatingButton}
          onClick={() => {
            onContestClick?.();
          }}
        >
          <div className={styles.contestBadgeContainer}>
            <div className={styles.contestBadge}>
              <div className={styles.contestBadgeText}>Конкурс 50 призов</div>
            </div>
            <div className={styles.contestTail} />
          </div>

          <img
            className={styles.contestPrizeImage}
            src={CupImage}
            alt="Приз конкурса"
          />
          <div className={styles.contestShadow} />
        </div>
      )}
      {!isScreenMode && isPremium && isEventReportsEnabled && (
        <AddMarkerButton onSelectType={handleAddMarker} theme={theme} />
      )}
      {/* Профиль в левом верхнем углу */}
      {!isScreenMode && (
        <ProfileFloatingButton
          onClick={() => {
            setIsMapFiltersOpen(false);
            onProfileClick?.();
          }}
          theme={theme}
          userName={userName}
          userAvatar={userAvatar}
          score={460}
          isGhostMode={userData?.ghost_mode_enabled ?? false}
        />
      )}
      {hasAccess && userPosition && (
        <UserLocationMarker
          map={mapInstance}
          mapgl={mapgl}
          isMapReady={isMapReady}
          position={userPosition}
          heading={userHeading}
        />
      )}
      <MapFilters
        isOpen={isMapFiltersOpen}
        onClose={() => setIsMapFiltersOpen(false)}
      />
      {isScreenMode && <ScreenshotWatermark />}
    </div>
  );
};
